import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Anchor,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  rem,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconExclamationMark, IconLock, IconMail } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import {
  CheckForExistingUserQuery,
  CheckForExistingUserQueryVariables,
  SignUpNewUserMutation,
  SignUpNewUserMutationVariables,
} from "../../../../gql/__generated__/graphql";
import { SIGN_UP_NEW_USER } from "../../../apis/lambda/GraphQL/User/mutations";
import { CHECK_FOR_EXISTING_USER } from "../../../apis/lambda/GraphQL/User/queries";
import { userInfoAtom } from "../../../atoms";
import {
  isLoggedIn as isLoggedInFunction,
  saveJWTCookie,
} from "../../../utils/jwtUtils";

interface SignupFormValues {
  email: string;
  password: string;
}

function SignupPage() {
  const navigate = useNavigate();

  // React State
  const [isSigningUp, setIsSigningUp] = useState(false);

  // Recoil State
  const setUserInfo = useSetRecoilState(userInfoAtom);

  // Form handling
  const form = useForm<SignupFormValues>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => {
        if (!value) return "Email is required";
        if (!/\S+@\S+\.\S+/.test(value)) return "Email is invalid";
        return null;
      },
      password: (value) => {
        if (isSigningUp && !value) return "Password is required";
        if (isSigningUp && value.length < 6)
          return "Password must be at least 6 characters";
        return null;
      },
    },
  });

  // GraphQL Queries
  const [checkExistingUser, { loading }] = useLazyQuery<
    CheckForExistingUserQuery,
    CheckForExistingUserQueryVariables
  >(CHECK_FOR_EXISTING_USER, {
    onCompleted: (data) => {
      const response = data.checkForExistingUser;
      if (!response.exists || response.validated) {
        setIsSigningUp(true);
      } else {
        notifications.show({
          title: "Email already registered",
          message:
            "This email is already registered. Please log in or use a different email.",
          color: "yellow",
          icon: <IconExclamationMark size={16} />,
        });
      }
    },
    onError: (error) => {
      notifications.show({
        title: "Error",
        message: "An error occurred while checking the email.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
    },
  });

  // GraphQL Mutations
  const [signUpNewUser, { loading: loadingSignUp }] = useMutation<
    SignUpNewUserMutation,
    SignUpNewUserMutationVariables
  >(SIGN_UP_NEW_USER);

  useEffect(() => {
    if (isLoggedInFunction()) {
      navigate("/");
    }
  }, [navigate]);

  function handleCheckEmail(values: SignupFormValues) {
    checkExistingUser({
      variables: {
        email: values.email,
      },
    });
  }

  async function handleSignup(values: SignupFormValues) {
    try {
      const response = await signUpNewUser({
        variables: {
          input: {
            email: values.email,
            password: values.password,
            userType: "CREATOR",
          },
        },
      });

      if (response.data) {
        const jwt = response.data.signUpNewUser;
        saveJWTCookie(jwt);
        setUserInfo({
          email: values.email,
          userType: "CREATOR",
        });
        navigate("/signup/complete", {
          state: {
            email: values.email,
          },
        });
      }
    } catch (error) {
      notifications.show({
        title: "Signup failed",
        message: "An error occurred during signup. Please try again.",
        color: "red",
        icon: <IconExclamationMark size={16} />,
      });
    }
  }

  const loadingSomething = loading || loadingSignUp;

  return (
    <Box
      style={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: rem(16),
      }}
    >
      <Container size="xs" w="100%">
        <Stack gap="xl" align="stretch" w="100%">
          <Title
            order={1}
            ta="center"
            style={{
              background:
                "linear-gradient(45deg, var(--mantine-color-tertiary-6), var(--mantine-color-yellow-6))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1.3,
            }}
          >
            Sign Up
          </Title>

          <Paper p="xl" radius="lg" shadow="md" withBorder>
            <form
              onSubmit={form.onSubmit(
                isSigningUp ? handleSignup : handleCheckEmail
              )}
            >
              <Stack gap="lg">
                <TextInput
                  label="Email"
                  placeholder="Enter your email"
                  leftSection={<IconMail size={16} />}
                  disabled={isSigningUp}
                  {...form.getInputProps("email")}
                />

                {isSigningUp && !loadingSomething && (
                  <TextInput
                    label="Password"
                    type="password"
                    placeholder="Create a password"
                    leftSection={<IconLock size={16} />}
                    {...form.getInputProps("password")}
                  />
                )}

                <Button
                  type="submit"
                  size="lg"
                  fullWidth
                  color="orange"
                  loading={loadingSomething}
                  leftSection={
                    loadingSomething ? <Loader size={16} /> : undefined
                  }
                >
                  Sign Up
                </Button>
              </Stack>
            </form>
          </Paper>

          <Group justify="center">
            <Text size="sm" c="gray.6">
              Already have an account?{" "}
              <Anchor component={Link} to="/login" c="orange.6" fw={600}>
                Log In
              </Anchor>
            </Text>
          </Group>
        </Stack>
      </Container>
    </Box>
  );
}

export default SignupPage;
